// World headlines from Deutsche Welle (RDF 1.0) and the New York Times (RSS 2.0). Both send
// Access-Control-Allow-Origin: *. A regex reader keeps this working outside the browser too.

import type { Fetcher } from '../types.ts';
import { getText } from './http.ts';
import type { LiveRecord } from './types.ts';

interface Outlet {
  id: string;
  publisher: string;
  url: string;
}

const OUTLETS: Outlet[] = [
  { id: 'dw', publisher: 'Deutsche Welle', url: 'https://rss.dw.com/rdf/rss-en-world' },
  { id: 'nyt', publisher: 'The New York Times', url: 'https://rss.nytimes.com/services/xml/rss/nyt/World.xml' },
];
const ITEMS_PER_OUTLET = 8;
const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

/** Text of the first <tag> inside `xml`, CDATA and entities resolved. */
function tag(xml: string, name: string): string | null {
  const m = new RegExp(`<${name}(?:\\s[^>]*)?>([\\s\\S]*?)</${name}>`).exec(xml);
  if (!m) {
    return null;
  }
  const raw = m[1].replace(/^\s*<!\[CDATA\[([\s\S]*?)\]\]>\s*$/, '$1').trim();
  return raw.replace(/&(#x?[0-9a-f]+|\w+);/gi, (whole, code: string) => {
    if (code[0] === '#') {
      const n = code[1].toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isFinite(n) ? String.fromCodePoint(n) : whole;
    }
    return ENTITIES[code] ?? whole;
  });
}

function parse(xml: string, outlet: Outlet): LiveRecord[] {
  const out: LiveRecord[] = [];

  for (const m of xml.matchAll(/<item(?:\s[^>]*)?>([\s\S]*?)<\/item>/g)) {
    const title = tag(m[1], 'title');
    const link = tag(m[1], 'link');
    const stamp = tag(m[1], 'pubDate') ?? tag(m[1], 'dc:date');
    const time = stamp ? new Date(stamp) : null;
    if (!title || !link || !time || Number.isNaN(time.getTime())) {
      continue;
    }
    out.push({ id: `${outlet.id}:${link}`, kind: 'headline', title, time: time.toISOString(), url: link, publisher: outlet.publisher });
  }
  return out.slice(0, ITEMS_PER_OUTLET);
}

/** Every outlet that answers; one being down does not hide the other. Rejects only if all fail. */
export async function worldHeadlines(fetcher: Fetcher): Promise<LiveRecord[]> {
  const settled = await Promise.allSettled(OUTLETS.map(async (o) => parse(await getText(fetcher, o.url), o)));
  const ok = settled.filter((s): s is PromiseFulfilledResult<LiveRecord[]> => s.status === 'fulfilled');
  if (ok.length === 0) {
    throw new Error('headlines: every outlet failed');
  }
  return ok.flatMap((s) => s.value);
}
